<template>
  <div
    ref="mainRef"
    class="flex flex-col items-center w-full max-w-xs gap-4"
  >
    <div class="header text-center">
      <span class="text-19px leading-24px font-semibold">
        {{ _t('package.edit') }}
      </span>

      <Icon
        name="close-circle"
        class="cursor-pointer absolute right-4 top-3 w-22px"
        @click="emit('done')"
      ></Icon>
    </div>

    <PackageCover
      :packageStyle="showingPackage.style"
      :size="106"
      :border-radius="8"
      @click="openCoverSelector"
    />

    <div class="w-full">
      <div
        class="text-15px leading-none text-[var(--text-color-secondary)] font-semibold mb-6px"
      >
        {{ _t('package.form_name') }}
      </div>

      <InputText
        v-model="name"
        :pt="{ root: { 'data-focus': 'true' } }"
        class="border-inputBorder bg-inputBg w-full"
        @keydown.enter="onFormSubmit"
      />
    </div>

    <Button
      class="w-full"
      :label="_t('common.save')"
      :disabled="!isValid"
      :loading="loading"
      @click="onFormSubmit"
    />
  </div>
</template>

<script lang="ts" setup>
import { updatePackage } from '@/api/package-source'
import { ref, onMounted, computed, watch } from 'vue'

import type { PackageBasic, PackageStyle } from '@/api/package-source'
import PackageCover from '@/components/business/package/PackageCover.vue'
import PkgEditCoverSelector from './PkgEditCoverSelector.vue'

const props = withDefaults(
  defineProps<{
    package: PackageBasic
    contentTagKey?: string
    showBack?: boolean
  }>(),
  {
    contentTagKey: undefined,
    showBack: false,
  }
)

const emit = defineEmits<{
  done: []
  back: []
  update: [PackageBasic]
  create: [PackageBasic]
}>()

const name = ref(props.package?.name ?? '')
const style = ref({ ...props.package?.style })

// 记录所有生成过的name->tags映射
const cachedPackageNameTags = ref<Record<string, string[] | null>>(
  props.package?.name && props.package?.style?.packageNameTags
    ? { [props.package.name]: props.package.style.packageNameTags }
    : {}
)

const showingPackage = computed<PackageBasic>(() => {
  return { ...props.package, style: style.value, name: name.value }
})

watch(name, (newName, _) => {
  // 检查新名字是否有缓存的tags
  if (cachedPackageNameTags.value[newName]) {
    // 有缓存，恢复对应的tags
    style.value = {
      ...style.value,
      packageNameTags: cachedPackageNameTags.value[newName],
    }
  } else {
    // 没有缓存，设为undefined
    style.value = {
      ...style.value,
      packageNameTags: undefined,
    }
  }
})

const isValid = computed(() => name.value !== '')

const loading = ref(false)

function openCoverSelector() {
  _openDialog(PkgEditCoverSelector, {
    rootClass: 'p-0 w-[98vw] min-w-0 max-w-sm! md:max-w-md! lg:max-w-lg!',
    dialog: {
      showHeader: false,
      dismissableMask: true,
      contentClass: 'px-2 py-2 sm:px-4 sm:py-3',
    },
    props: {
      package: showingPackage.value,
      onUpdate: (newStyle: PackageStyle) => {
        // 保存当前名字对应的新tags到缓存
        if (
          newStyle.packageNameTags !== cachedPackageNameTags.value[name.value]
        ) {
          cachedPackageNameTags.value[name.value] =
            newStyle.packageNameTags || null

          // 如果当前名字是原始包名，emit update
          if (name.value === props.package?.name) {
            emit('update', {
              ...props.package,
              style: {
                ...props.package.style,
                packageNameTags: newStyle.packageNameTags,
              },
            })
          }
        }
        style.value = newStyle
      },
    },
  })
}

function onFormSubmit() {
  if (!isValid.value) return

  loading.value = true

  updatePackage(props.package.id, {
    name: name.value.trim(),
    style: style.value,
  })
    .then(res => {
      if (res.code !== 0) {
        _message.info(res.message)
        return
      }

      _message.success('更新成功')
      emit('update', res.data)
      emit('done')
    })
    .finally(() => {
      loading.value = false
    })
}

// auto focus
const mainRef = ref()
onMounted(() => {
  setTimeout(() => {
    mainRef.value.querySelector('input[data-focus]').focus()
  }, 0)
})
</script>

<style scoped></style>
